import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { serviceService } from '../serviceService';

export function useServices(page = 1, limit = 20, search?: string, serviceType?: string, enabled = true) {
  return useQuery({
    queryKey: ['services', page, limit, search, serviceType],
    queryFn: () => serviceService.getServices(page, limit, search, serviceType),
    placeholderData: (previous) => previous,
    enabled,
  });
}

export function useService(id?: string) {
  return useQuery({
    queryKey: ['service', id],
    queryFn: () => serviceService.getService(id!),
    enabled: !!id,
  });
}

export function useCreateService() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: serviceService.createService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
    },
  });
}

export function useUpdateService() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => serviceService.updateService(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
    },
  });
}

export function useDeleteService() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => serviceService.deleteService(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      queryClient.invalidateQueries({ queryKey: ['services-trash'] });
    },
  });
}

/** Bulk-delete services as a single silent mutation (loops the single-delete endpoint). */
export function useBulkDeleteServices() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        await serviceService.deleteService(id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      queryClient.invalidateQueries({ queryKey: ['services-trash'] });
    },
  });
}

/** Bulk-update services (e.g. activate/deactivate) as a single silent mutation. */
export function useBulkUpdateServices() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: async ({ ids, data }: { ids: string[]; data: any }) => {
      for (const id of ids) {
        await serviceService.updateService(id, data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
    },
  });
}

/** List soft-deleted services (the trash view). */
export function useTrashedServices(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: ['services-trash', page, limit, search],
    queryFn: () => serviceService.getTrashedServices(page, limit, search),
    placeholderData: (previous) => previous,
  });
}

/** Restore trashed services back to the active list. */
export function useRestoreServices() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => serviceService.restoreServices(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      queryClient.invalidateQueries({ queryKey: ['services-trash'] });
    },
  });
}

/** Permanently delete trashed services. */
export function usePermanentlyDeleteServices() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => serviceService.permanentlyDeleteServices(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      queryClient.invalidateQueries({ queryKey: ['services-trash'] });
    },
  });
}

/** Force-delete trashed services together with every linked record. */
export function useForceDeleteServices() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: (ids: string[]) => serviceService.forceDeleteServices(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      queryClient.invalidateQueries({ queryKey: ['services-trash'] });
    },
  });
}

export function useServiceTypes(page = 1, limit = 20, search?: string) {
  return useQuery({
    queryKey: ['service-types', page, limit, search],
    queryFn: () => serviceService.getServiceTypes(page, limit, search),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useActiveServiceTypes() {
  return useQuery({
    queryKey: ['service-types', 'active'],
    queryFn: () => serviceService.getActiveServiceTypes(),
    staleTime: 30000,
  });
}

export function useServiceType(id?: string) {
  return useQuery({
    queryKey: ['service-type', id],
    queryFn: () => serviceService.getServiceType(id!),
    enabled: !!id,
    staleTime: 30000,
  });
}

export function useCreateServiceType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: serviceService.createServiceType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-types'] });
    },
  });
}

export function useUpdateServiceType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => serviceService.updateServiceType(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['service-types'] });
      queryClient.invalidateQueries({ queryKey: ['service-type', variables.id] });
    },
  });
}

export function useDeleteServiceType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => serviceService.deleteServiceType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-types'] });
    },
  });
}

/** Bulk-delete service types as a single silent mutation (loops the single-delete endpoint). */
export function useBulkDeleteServiceTypes() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        await serviceService.deleteServiceType(id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-types'] });
    },
  });
}
